import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Building2,
  CreditCard,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import HomeSmartWhiteLabelSettings from './HomeSmartWhiteLabelSettings';

const MioProfilo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isExternal, setIsExternal] = useState(() => {
    try {
      return !!JSON.parse(localStorage.getItem('hw_user') || '{}').is_external_tenant;
    } catch {
      return false;
    }
  });
  const [profile, setProfile] = useState({
    // Dati personali
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    cap: '',
    fiscal_code: '',
    // Dati bancari
    bank_name: '',
    account_holder: '',
    iban: '',
    bic_swift: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setIsExternal(!!response.data.is_external_tenant);
        setProfile(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const payload = isExternal
        ? Object.fromEntries(Object.entries(profile).filter(([key]) => !['bank_name', 'account_holder', 'iban', 'bic_swift'].includes(key)))
        : profile;
      await axios.put(
        `${API}/api/user/profile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profilo salvato con successo!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Errore nel salvataggio del profilo');
    } finally {
      setSaving(false);
    }
  };

  const formatIBAN = (value) => {
    // Remove spaces and convert to uppercase
    const clean = value.replace(/\s/g, '').toUpperCase();
    // Add space every 4 characters
    return clean.replace(/(.{4})/g, '$1 ').trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-slate-600">Caricamento profilo...</span>
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-6 max-w-4xl mx-auto" data-testid="mio-profilo">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Il Mio Profilo</h1>
        <p className="text-slate-500 mt-1" data-testid="profile-description">{isExternal ? 'Gestisci i tuoi dati personali' : 'Gestisci i tuoi dati personali e le coordinate bancarie'}</p>
      </div>

      {/* Dati Personali */}
      <Card data-testid="personal-data-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            Dati Personali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  value={profile.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="pl-10"
                  placeholder="Mario Rossi"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={profile.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="pl-10 bg-slate-50"
                  disabled
                  data-testid="profile-email-input"
                />
              </div>
              <p className="text-xs text-slate-400">L'email non può essere modificata</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Telefono</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="phone"
                  value={profile.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="pl-10"
                  placeholder="+39 333 1234567"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fiscal_code">Codice Fiscale</Label>
              <Input
                id="fiscal_code"
                value={profile.fiscal_code || ''}
                onChange={(e) => handleChange('fiscal_code', e.target.value.toUpperCase())}
                placeholder="RSSMRA80A01H501Z"
                maxLength={16}
                data-testid="profile-fiscal-code-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="address">Indirizzo</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="address"
                  value={profile.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="pl-10"
                  placeholder="Via Roma 1"
                  data-testid="profile-address-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cap">CAP</Label>
              <Input
                id="cap"
                value={profile.cap || ''}
                onChange={(e) => handleChange('cap', e.target.value)}
                placeholder="00100"
                maxLength={5}
                data-testid="profile-cap-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">Città</Label>
            <Input
              id="city"
              value={profile.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Roma"
              data-testid="profile-city-input"
            />
          </div>
        </CardContent>
      </Card>

      {isExternal && <HomeSmartWhiteLabelSettings />}

      {/* Dati Bancari */}
      {!isExternal && <Card data-testid="bank-details-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            Coordinate Bancarie
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Inserisci i dati del conto su cui desideri ricevere i pagamenti
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank_name">Nome Banca</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="bank_name"
                  value={profile.bank_name || ''}
                  onChange={(e) => handleChange('bank_name', e.target.value)}
                  className="pl-10"
                  placeholder="Intesa Sanpaolo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_holder">Intestatario Conto</Label>
              <Input
                id="account_holder"
                value={profile.account_holder || ''}
                onChange={(e) => handleChange('account_holder', e.target.value)}
                placeholder="Mario Rossi"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="iban">IBAN</Label>
            <Input
              id="iban"
              value={profile.iban || ''}
              onChange={(e) => handleChange('iban', formatIBAN(e.target.value))}
              placeholder="IT60 X054 2811 1010 0000 0123 456"
              className="font-mono"
            />
            <p className="text-xs text-slate-400">
              L'IBAN italiano inizia con IT e ha 27 caratteri
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bic_swift">BIC/SWIFT</Label>
            <Input
              id="bic_swift"
              value={profile.bic_swift || ''}
              onChange={(e) => handleChange('bic_swift', e.target.value.toUpperCase())}
              placeholder="BCITITMM"
              className="font-mono"
              maxLength={11}
            />
          </div>

          {/* Status indicator */}
          {profile.iban && profile.bic_swift ? (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">Coordinate bancarie complete</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg text-orange-700">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Completa le coordinate bancarie per ricevere i pagamenti</span>
            </div>
          )}
        </CardContent>
      </Card>}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          size="lg"
          data-testid="profile-save-button"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salva Modifiche
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default MioProfilo;
